import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { toast } from 'react-toastify';
import './CommonStyles.css';
import './AllSellStyles.css';
import './NotificationTemplates.css';
import apiService from '../../services/api';

const TemplateEditor = ({ templateKey, templateData, onSave, saving }) => {
  const data = templateData || {};
  const extraTags = data.extra_tags ? data.extra_tags.flat() : [];

  const [subject, setSubject] = useState(data.subject || '');
  const [cc, setCc] = useState(data.cc || '');
  const [bcc, setBcc] = useState(data.bcc || '');
  const [emailBody, setEmailBody] = useState(data.email_body || '<p></p>');
  const [sms, setSms] = useState(data.sms_body || '');
  const [whatsapp, setWhatsapp] = useState(data.whatsapp_text || '');
  const [autoEmail, setAutoEmail] = useState(!!data.auto_send);
  const [autoSms, setAutoSms] = useState(!!data.auto_send_sms);
  const [autoWhatsapp, setAutoWhatsapp] = useState(!!data.auto_send_wa_notif);

  useEffect(() => {
    setSubject(data.subject || '');
    setCc(data.cc || '');
    setBcc(data.bcc || '');
    setEmailBody(data.email_body || '<p></p>');
    setSms(data.sms_body || '');
    setWhatsapp(data.whatsapp_text || '');
    setAutoEmail(!!data.auto_send);
    setAutoSms(!!data.auto_send_sms);
    setAutoWhatsapp(!!data.auto_send_wa_notif);
  }, [templateKey, data]);

  const handleSave = () => {
    onSave(templateKey, {
      subject,
      email_body: emailBody,
      sms_body: sms,
      whatsapp_text: whatsapp,
      auto_send: autoEmail ? 1 : 0,
      auto_send_sms: autoSms ? 1 : 0,
      auto_send_wa_notif: autoWhatsapp ? 1 : 0,
      cc,
      bcc
    });
  };

  return (
    <div className="notification-content">
      <div className="editor-tags-section">
        <h4 className="editor-tags-title">Available Tags:</h4>
        <div className="editor-tags-list">
          {extraTags.map((tag, i) => (
            <span key={i} className="editor-tag-pill">{tag}</span>
          ))}
        </div>
        <p className="editor-tags-hint">Business logo will not work in SMS</p>
      </div>

      <div className="editor-form-grid">
        <div className="editor-form-group">
          <label className="editor-label">Email Subject:*</label>
          <input type="text" className="editor-input" value={subject} onChange={e => setSubject(e.target.value)} />
        </div>

        <div className="editor-form-group">
          <label className="editor-label">CC:</label>
          <input type="text" className="editor-input" value={cc} onChange={e => setCc(e.target.value)} placeholder="CC" />
        </div>

        <div className="editor-form-group">
          <label className="editor-label">BCC:</label>
          <input type="text" className="editor-input" value={bcc} onChange={e => setBcc(e.target.value)} placeholder="BCC" />
        </div>

        <div className="editor-form-group quill-container">
          <label className="editor-label">Email Body:*</label>
          <ReactQuill
            theme="snow"
            value={emailBody}
            onChange={setEmailBody}
            modules={{
              toolbar: [
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'image'],
                ['clean']
              ]
            }}
          />
        </div>

        <div className="editor-form-group">
          <label className="editor-label">SMS Body:*</label>
          <textarea className="editor-textarea" value={sms} onChange={e => setSms(e.target.value)} rows={4} />
        </div>

        <div className="editor-form-group">
          <label className="editor-label">Whatsapp Text:</label>
          <textarea className="editor-textarea" value={whatsapp} onChange={e => setWhatsapp(e.target.value)} placeholder="Whatsapp Text" rows={4} />
        </div>

        <div className="auto-send-section">
          <label className="auto-send-label">
            <input type="checkbox" className="auto-send-checkbox" checked={autoEmail} onChange={e => setAutoEmail(e.target.checked)} />
            Auto Send Email
          </label>
          <label className="auto-send-label">
            <input type="checkbox" className="auto-send-checkbox" checked={autoSms} onChange={e => setAutoSms(e.target.checked)} />
            Auto Send SMS
          </label>
          <label className="auto-send-label">
            <input type="checkbox" className="auto-send-checkbox" checked={autoWhatsapp} onChange={e => setAutoWhatsapp(e.target.checked)} />
            Auto send Whatsapp notification
          </label>
        </div>

        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'center' }}>
          <button className="editor-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationTemplates = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState({
    general_notifications: {},
    customer_notifications: {},
    supplier_notifications: {}
  });
  const [activeTab, setActiveTab] = useState('general');
  const [activeSubTab, setActiveSubTab] = useState('');

  const tabMap = {
    general: 'general_notifications',
    customer: 'customer_notifications',
    supplier: 'supplier_notifications'
  };

  const updateTemplatesState = (resData) => {
    setTemplates({
      general_notifications: resData.general_notifications || {},
      customer_notifications: resData.customer_notifications || {},
      supplier_notifications: resData.supplier_notifications || {}
    });
  };

  useEffect(() => {
    let cancelled = false;

    const fetchTemplates = async () => {
      try {
        const res = await apiService.getNotificationTemplates();
        if (cancelled) return;
        if (res.success && res.data) {
          updateTemplatesState(res.data);
        }
      } catch (err) {
        if (cancelled) return;
      /* console.error('Error loading notification templates:', err); */
        toast.error('Failed to load notification templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTemplates();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentKey = tabMap[activeTab];
    const keys = Object.keys(templates[currentKey] || {});
    if (keys.length > 0 && (!activeSubTab || !templates[currentKey][activeSubTab])) {
      setActiveSubTab(keys[0]);
    }
  }, [activeTab, templates, activeSubTab]);

  const handleTopTabChange = (tabId) => {
    setActiveTab(tabId);
    const currentKey = tabMap[tabId];
    const keys = Object.keys(templates[currentKey] || {});
    setActiveSubTab(keys[0] || '');
  };

  const handleSaveTemplate = async (templateFor, templateData) => {
    setSaving(true);
    try {
      const payload = { [templateFor]: templateData };
      const res = await apiService.saveNotificationTemplates(payload);
      if (res.success) {
        toast.success('Template saved successfully');
        const reloadRes = await apiService.getNotificationTemplates();
        if (reloadRes.success && reloadRes.data) {
          updateTemplatesState(reloadRes.data);
        }
      } else {
        toast.error(res.message || 'Failed to save template');
      }
    } catch (err) {
      /* console.error('Error saving template:', err); */
      toast.error(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const topTabs = [
    { id: 'general', label: 'Notifications' },
    { id: 'customer', label: 'Customer Notifications' },
    { id: 'supplier', label: 'Supplier Notifications' }
  ];

  const currentKey = tabMap[activeTab];
  const currentNotifications = templates[currentKey] || {};
  const currentTemplate = currentNotifications[activeSubTab] || {};

  const subTabs = Object.entries(currentNotifications).map(([key, val]) => ({
    id: key,
    label: val.name || key
  }));

  if (loading) {
    return (
      <div className="notification-templates-container">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Loading notification templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-templates-container">
      <div className="notification-layout">
        <div className="notification-top-tabs">
          {topTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTopTabChange(tab.id)}
              className={`notification-top-tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="notification-body">
          <div className="notification-sidebar">
            {subTabs.map(subTab => (
              <button
                key={subTab.id}
                onClick={() => setActiveSubTab(subTab.id)}
                className={`notification-sidebar-btn ${activeSubTab === subTab.id ? 'active' : ''}`}
              >
                {subTab.label}
              </button>
            ))}
          </div>

          {activeSubTab && (
            <TemplateEditor
              templateKey={activeSubTab}
              templateData={currentTemplate}
              onSave={handleSaveTemplate}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationTemplates;
